import { produce } from 'immer'
import { TimeWindow } from '@dhl/inputs'
import { ServicepointInputValue } from '@dhl/servicepoint-input'
import { ACTION, Action } from './actions'

import { CARRIER } from '~/basket/model/constants'
import { getCountryCodeByCurrentDomain } from '~/util/domain/getCountryCodeByCurrentDomain'

/** *****************
 * Reducers
 ***************** */

export type Package = {
  trackerCode: string
  destinationCountry: string
  carrierKey: string
  parcelTypeKey: string | null
}

export type Address = {
  countryCode: string
  postalCode: string
  city: string
  street: string
  houseNumber: string
  houseNumberSuffix: string
  suburb: string
  additionalAddressLine?: string
  line?: string[]
}

export type Receiver = {
  firstName: string
  lastName: string
  companyName: string
  address: Address
  email: string
  phoneNumber: string
  deliveryOptionKey: string | null
  serviceOptionKeys: string[]
  servicepoint: ServicepointInputValue | null
  personalNote: string
  reference: string
  insuranceValue: number | null | undefined
  saveToAddressBook: boolean
  usedAddressBookEntryId: string | null
  contentDescription: string | null
}

export type PickupRange = {
  min: number
  max: number
}

export type Shipper = {
  firstName: string
  lastName: string
  companyName: string
  vatNumber: string
  eoriNumber: string
  address: Address
  email: string
  phoneNumber: string
}

export type CustomsItem = {
  description: string
  quantity: number
  weight: number
  value: number
  commodityCode: string
  countryOfOrigin: string
}

export enum CustomsType {
  Commercial = 'commercial',
  NonCommercial = 'noncommercial',
}

export type Customs = {
  customsType: CustomsType
  exportType: string
  exportReason: string
  items: CustomsItem[]
  remarks: string
  license: string
  certificate: string
  invoice: string
  isExpressPaperlessTrade?: boolean
  senderInboundVatNumber?: string
  shippingFeeValue?: number
  incoTerms?: string
}

export type ShipmentState = {
  id: number
  confirmedSteps: string[]
  package: Package
  receiver: Receiver
  shipper: Shipper
  customs: Customs
  promocode: Promocode
}

export type ShippingFee = {
  currency: string
  value: number
}

export const defaultAddress: Address = {
  countryCode: '',
  postalCode: '',
  city: '',
  street: '',
  houseNumber: '',
  houseNumberSuffix: '',
  suburb: '',
  additionalAddressLine: '',
}

export const defaultReceiver: Receiver = {
  firstName: '',
  lastName: '',
  companyName: '',
  address: defaultAddress,
  email: '',
  phoneNumber: '',
  deliveryOptionKey: null,
  serviceOptionKeys: [],
  servicepoint: null,
  reference: '',
  personalNote: '',
  insuranceValue: undefined,
  saveToAddressBook: false,
  usedAddressBookEntryId: null,
  contentDescription: '',
}

export const defaultShipper: Shipper = {
  firstName: '',
  lastName: '',
  companyName: '',
  vatNumber: '',
  eoriNumber: '',
  address: defaultAddress,
  email: '',
  phoneNumber: '',
}

export const defaultCustomsItem: CustomsItem = {
  description: '',
  quantity: 0,
  weight: 0,
  value: 0,
  commodityCode: '',
  countryOfOrigin: '',
}

export const defaultCustoms: Customs = {
  customsType: CustomsType.NonCommercial,
  exportType: '',
  exportReason: '',
  items: [defaultCustomsItem],
  remarks: '',
  license: '',
  certificate: '',
  invoice: '',
}

const shipmentReducer = (state: Readonly<ShipmentState>, action: Action): ShipmentState => {
  switch (action.type) {
    case ACTION.UPDATE_PACKAGE: {
      if (action.payload.options.hasUpdatedDestinationCountry) {
        return produce<ShipmentState>(state, (draft) => {
          // eslint-disable-next-line no-param-reassign
          draft.package = {
            ...state.package,
            ...action.payload.package,
          }
          // eslint-disable-next-line no-param-reassign
          draft.package.parcelTypeKey = null
          // eslint-disable-next-line no-param-reassign
          draft.receiver = defaultReceiver
          // eslint-disable-next-line no-param-reassign
          draft.confirmedSteps = []
        })
      }

      if (action.payload.options.hasUpdatedCarrierKey) {
        return {
          ...state,
          package: {
            ...state.package,
            ...action.payload.package,
            parcelTypeKey: null,
          },
        }
      }

      return {
        ...state,
        package: {
          ...state.package,
          ...action.payload.package,
        },
      }
    }
    case ACTION.CONFIRM_STEP: {
      return {
        ...state,
        confirmedSteps: [...new Set([...state.confirmedSteps, action.payload.stepName])],
      }
    }
    case ACTION.UPDATE_RECEIVER: {
      return {
        ...state,
        receiver: {
          ...state.receiver,
          ...action.payload.receiver,
        },
      }
    }
    case ACTION.UPDATE_SHIPPER: {
      return {
        ...state,
        shipper: {
          ...state.shipper,
          ...action.payload.shipper,
        },
      }
    }
    case ACTION.UPDATE_CUSTOMS: {
      return {
        ...state,
        customs: {
          ...state.customs,
          ...action.payload.customs,
        },
      }
    }
    case ACTION.UPDATE_PROMOCODE: {
      return {
        ...state,
        promocode: {
          ...state.promocode,
          ...action.payload.promocode,
        },
      }
    }
    case ACTION.DELETE_PROMOCODE: {
      return {
        ...state,
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        promocode: defaultPromocode,
      }
    }
    default: {
      return state
    }
  }
}

export type MarketingPreference = {
  wantsNewsAndPromotions: boolean
}

export type Config = {
  isParcelshopPaymentEnabled: boolean
  isAutocompleteEnabled: boolean
  isEmailSubscriptionEnabled: boolean
  isPromocodeEnabled: boolean
  isDefaultParcelTypeKeyEnabled: boolean
  isAddressBookEnabled: boolean
  isTrackerCodeEnabled: boolean
  isEmailReceiverRequired: boolean
  isSpaServiceAvailable: boolean
  isCustomsApplicable: boolean
  defaultDestinationCountry: string
  defaultCarrier: string
  defaultDeliveryOptionKey: string | null
  preFilledParcelTypeKey: string | null
  preFilledReceiver: PreFilledReceiver
  preFilledShipper: PreFilledShipper
}

export type PreFilledReceiver = {
  firstName: string
  lastName: string
  postalCode: string
  houseNumber: string
  houseNumberSuffix: string
  email: string
  phoneNumber: string
  deliveryOptionKey: string | null
}

export type PreFilledShipper = {
  firstName: string
  lastName: string
  postalCode: string
  houseNumber: string
  houseNumberSuffix: string
  email: string
  phoneNumber: string
  companyName: string
  vatNumber: string
}

export enum PromocodeStatus {
  Empty,
  Valid,
  Invalid,
  Unknown,
}

export type Discount = {
  percentage: number
  destinationCountries: string[]
  carrier: string | null
}

export type Promocodes = Record<number, Promocode>

export type Captchas = Record<number, string | null>

export type Promocode = {
  value: string
  status: PromocodeStatus
  discount: Discount | null
  maxUses: number
}

export type Payment = {
  handOffKey: string | null
  pickupDate: string | null
  pickupRange: PickupRange | null
  timeWindow: TimeWindow | null
  paymentType: string | null
  usePaymentService: boolean
  issuerId: string | null
  emailSubscription: boolean
  paymentOptionKey: string
  agreeConditions: boolean
}

export type SessionState = {
  config: Config
  nextShipmentId: number
  selectedShipmentId: number | null
  shipments: ShipmentState[]
  originCountry: string
  orderSubmitFailed: boolean
  payment: Payment
  isSubmitting: boolean
  promoCodes: Record<number, Promocode>
  captchas: Record<number, string | null>
  hideMarketingPreference: boolean
}

export const defaultPromocode: Promocode = {
  value: '',
  status: PromocodeStatus.Empty,
  discount: null,
  maxUses: 0,
}

export const defaultPayment: Payment = {
  handOffKey: null,
  pickupDate: null,
  pickupRange: null,
  timeWindow: null,
  paymentType: null,
  usePaymentService: false,
  issuerId: '',
  emailSubscription: false,
  paymentOptionKey: '',
  agreeConditions: false,
}

const initialSession: SessionState = {
  config: {
    isParcelshopPaymentEnabled: false,
    isAutocompleteEnabled: true,
    isEmailSubscriptionEnabled: true,
    isPromocodeEnabled: true,
    isDefaultParcelTypeKeyEnabled: false,
    isAddressBookEnabled: false,
    isTrackerCodeEnabled: false,
    isEmailReceiverRequired: true,
    isSpaServiceAvailable: false,
    isCustomsApplicable: false,
    defaultCarrier: CARRIER.PARCEL,
    defaultDestinationCountry: 'NL',
    defaultDeliveryOptionKey: null,
    preFilledParcelTypeKey: null,
    preFilledReceiver: {
      firstName: '',
      lastName: '',
      postalCode: '',
      houseNumber: '',
      houseNumberSuffix: '',
      phoneNumber: '',
      email: '',
      deliveryOptionKey: null,
    },
    preFilledShipper: {
      firstName: '',
      lastName: '',
      postalCode: '',
      houseNumber: '',
      houseNumberSuffix: '',
      phoneNumber: '',
      email: '',
      companyName: '',
      vatNumber: '',
    },
  },
  originCountry: getCountryCodeByCurrentDomain(),
  orderSubmitFailed: false,
  nextShipmentId: 0,
  selectedShipmentId: null,
  shipments: [],
  payment: defaultPayment,
  isSubmitting: false,
  hideMarketingPreference: false,
  promoCodes: { 0: defaultPromocode },
  captchas: { 0: null },
}

// eslint-disable-next-line @typescript-eslint/default-param-last
export const reducers = (state: SessionState = initialSession, action: Action): SessionState => {
  switch (action.type) {
    case ACTION.INITIALIZE_BASKET: {
      return {
        ...initialSession,
        config: {
          ...state.config,
          ...action.payload.config,
        },
        originCountry: action.payload.originCountry,
      }
    }
    case ACTION.CLEAR_BASKET: {
      return {
        ...initialSession,
        config: state.config,
        originCountry: state.originCountry,
      }
    }
    case ACTION.PREFILL_AFTER_FAILED_PAYMENT: {
      return { ...action.payload, isSubmitting: false, orderSubmitFailed: false }
    }
    case ACTION.UPDATE_BASKET_CONFIG: {
      return {
        ...state,
        config: {
          ...state.config,
          ...action.payload.config,
        },
      }
    }
    case ACTION.ORDER_SUBMITTED:
      return {
        ...state,
        isSubmitting: true,
        orderSubmitFailed: false,
      }
    case ACTION.ORDER_COMPLETED:
      return {
        ...state,
        isSubmitting: false,
      }
    case ACTION.ORDER_FAILED:
      return {
        ...state,
        isSubmitting: false,
        orderSubmitFailed: true,
      }
    case ACTION.CONFIRM_STEP:
    case ACTION.UPDATE_PACKAGE:
    case ACTION.UPDATE_RECEIVER:
    case ACTION.UPDATE_SHIPPER:
    case ACTION.UPDATE_CUSTOMS:
      return {
        ...state,
        shipments: state.shipments.map((shipment) =>
          shipment.id === state.selectedShipmentId ? shipmentReducer(shipment, action) : shipment
        ),
      }
    case ACTION.UPDATE_PROMOCODE: {
      const changedPromocodes = {
        ...state.promoCodes,
        [action.payload.shipmentId]: action.payload.promocode,
      }
      const changedCaptchas = {
        ...state.captchas,
        [action.payload.shipmentId]: null,
      }
      return {
        ...state,
        promoCodes: changedPromocodes,
        captchas: changedCaptchas,
        shipments: state.shipments.map((shipment) =>
          shipment.id === action.payload.shipmentId ? shipmentReducer(shipment, action) : shipment
        ),
      } as SessionState
    }
    case ACTION.DELETE_PROMOCODE: {
      return {
        ...state,
        promoCodes: {
          ...state.promoCodes,
          [action.payload.shipmentId]: defaultPromocode,
        },
        shipments: state.shipments.map((shipment) =>
          shipment.id === action.payload.shipmentId ? shipmentReducer(shipment, action) : shipment
        ),
      }
    }
    case ACTION.ADD_SHIPMENT: {
      const defaultCarrier =
        state.shipments.length > 0 && state.shipments[0].package.carrierKey
          ? state.shipments[0].package.carrierKey
          : state.config.defaultCarrier

      const appendPromocode = (nexShipmentId: number): Promocodes => {
        if (state.shipments.length >= 1) {
          return {
            ...state.promoCodes,
            [nexShipmentId]: defaultPromocode,
          }
        }
        return {
          ...state.promoCodes,
        }
      }
      return {
        ...state,
        nextShipmentId: state.nextShipmentId + 1,
        selectedShipmentId: state.nextShipmentId,
        shipments: [
          ...state.shipments,
          {
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            ...createDefaultShipment({
              ...state.config,
              defaultCarrier,
              defaultDestinationCountry:
                state.shipments.length > 0
                  ? state.shipments[0].package.destinationCountry
                  : state.config.defaultDestinationCountry,
            }),
            id: state.nextShipmentId,
          },
        ],
        promoCodes: appendPromocode(state.nextShipmentId),
      }
    }

    case ACTION.REMOVE_SHIPMENT:
      /*
       * I kept the behaviour of `REMOVE_SHIPMENT` in sync with the old implementation but I would like to make
       * some changes here:
       *
       * - Don't update the selectedShipmentId if the removed shipment is not selected
       *
       * Besides this, I think it is a bit weird to add an empty shipment if the last shipment is removed. This
       * is done because it is not possible to see an empty basket on the overview page. The app currently
       * redirects to the package page if one removes the last shipment and a new empty shipment is needed for the
       * package page.
       */
      // eslint-disable-next-line no-case-declarations
      const newShipments = state.shipments.filter((shipment) => shipment.id !== action.payload.shipmentId)
      // eslint-disable-next-line no-case-declarations
      const removePromocode = (shipmentId: number): Promocodes => ({
        ...state.promoCodes,
        [shipmentId]: defaultPromocode,
      })
      // eslint-disable-next-line no-case-declarations
      const removeCaptcha = (shipmentId: number): Captchas => ({
        ...state.captchas,
        [shipmentId]: null,
      })

      if (newShipments.length === 0) {
        return {
          ...state,
          nextShipmentId: 1,
          selectedShipmentId: 0,
          shipments: [
            {
              // eslint-disable-next-line @typescript-eslint/no-use-before-define
              ...createDefaultShipment(state.config),
              id: 0,
            },
          ],
          payment: {
            ...state.payment,
          },
          promoCodes: removePromocode(action.payload.shipmentId),
          captchas: removeCaptcha(action.payload.shipmentId),
        }
      }

      return {
        ...state,
        shipments: newShipments,
        selectedShipmentId: Math.max(...newShipments.map((shipment) => shipment.id)),
        promoCodes: removePromocode(action.payload.shipmentId),
        captchas: removeCaptcha(action.payload.shipmentId),
      }
    case ACTION.SELECT_SHIPMENT:
      return {
        ...state,
        selectedShipmentId: action.payload.shipmentId,
      }
    case ACTION.UPDATE_PAYMENT: {
      return {
        ...state,
        payment: {
          ...state.payment,
          ...action.payload.payment,
          // If user has a profile and has set to receive marketing preferences, we should ignore the result
          // from the update_payment action and use the value from the profile.
          emailSubscription: (state.hideMarketingPreference || action.payload.payment.emailSubscription) ?? false,
        },
      }
    }
    case ACTION.FETCH_MKT_PREFERENCE_SUCCESS: {
      return {
        ...state,
        hideMarketingPreference: action.payload.wantsNewsAndPromotions,
      }
    }
    case ACTION.FETCH_MKT_PREFERENCE_FAILURE: {
      return {
        ...state,
        hideMarketingPreference: action.payload.wantsNewsAndPromotions,
      }
    }
    default: {
      return state
    }
  }
}

const createDefaultShipment = (config: Config) => ({
  confirmedSteps: [],
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  package: createDefaultPackage(config),
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  receiver: createDefaultReceiver(config),
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  shipper: createDefaultShipper(config),
  customs: defaultCustoms,
  promocode: defaultPromocode,
})

const createDefaultPackage = (config: Config) => ({
  trackerCode: '',
  destinationCountry: config.defaultDestinationCountry,
  carrierKey: config.defaultCarrier,
  parcelTypeKey: config.preFilledParcelTypeKey,
})

const createDefaultReceiver = (config: Config) => ({
  ...defaultReceiver,
  address: {
    ...defaultReceiver.address,
    postalCode: config.preFilledReceiver.postalCode,
    houseNumber: config.preFilledReceiver.houseNumber,
    houseNumberSuffix: config.preFilledReceiver.houseNumberSuffix,
  },
  deliveryOptionKey: config.preFilledReceiver.deliveryOptionKey || config.defaultDeliveryOptionKey,
  firstName: config.preFilledReceiver.firstName,
  lastName: config.preFilledReceiver.lastName,
  email: config.preFilledReceiver.email,
  phoneNumber: config.preFilledReceiver.phoneNumber,
})

const createDefaultShipper = (config: Config) => ({
  ...defaultShipper,
  address: {
    ...defaultShipper.address,
    postalCode: config.preFilledShipper.postalCode,
    houseNumber: config.preFilledShipper.houseNumber,
    houseNumberSuffix: config.preFilledShipper.houseNumberSuffix,
  },
  firstName: config.preFilledShipper.firstName,
  lastName: config.preFilledShipper.lastName,
  email: config.preFilledShipper.email,
  phoneNumber: config.preFilledShipper.phoneNumber,
  companyName: config.preFilledShipper.companyName,
  vatNumber: config.preFilledShipper.vatNumber,
})

/** *****************
 * Selectors
 ***************** */

function getSelectedShipment(state: SessionState): ShipmentState | null {
  const selectedShipment = state.shipments.find((shipment) => shipment.id === state.selectedShipmentId)
  return selectedShipment || null
}

function getOriginCountry(state: SessionState): string {
  return state.originCountry
}

function getMarketingPreference(state: SessionState): boolean {
  return state.hideMarketingPreference
}

export const selectors = {
  getOriginCountry,
  getSelectedShipment,
  getMarketingPreference,
}
